"use client";

/**
 * The property listing card.
 *
 * A property site is not a boutique grid with houses in it. What differs,
 * and why:
 *
 *  - A LANDSCAPE photo. A house in a square crop loses its compound.
 *  - The PRICE leads, with "/month" on a rent listing — the first thing
 *    anyone asks, and the thing that decides whether they read on.
 *  - A SPEC STRIP of four: beds · baths · m² · parking. Land swaps it for
 *    plot size · tenure · title · road, because nobody asks a plot how many
 *    bathrooms it has.
 *  - The LOCATION line carries the landmark, not the postcode — "600 m off
 *    Kira–Kasangati road" is how a Ugandan buyer places a property.
 *  - The owner and the agent's commission are never shown.
 *  - A price the owner set in another currency shows under the UGX one, and a
 *    plot reads in decimals or acres with m² under it (Nakasero Estates, Oct
 *    2026). The card knows nothing of the brand's rates, so it never works a
 *    price out — the listing page does.
 *  - A let's short house rules in one line — "Up to 4 people · No children"
 *    (Hoxton Kesington, Oct 2026). The owner's own rules are on its page.
 */

import Link from "next/link";
import { Bath, BedDouble, Camera, Car, LandPlot, Maximize2, MapPin, Ruler, ScrollText, Users, type LucideIcon } from "lucide-react";
import type { ShopProduct } from "@/lib/types/shop";
import {
  PROPERTY_PLACEHOLDER_IMAGE,
  houseRuleChips,
  isForRent,
  intervalPer,
  propertyTypeLabel,
  tenureLabel,
} from "@/lib/config/property";
import { cleanForeignPrice, formatIn } from "@/lib/config/currencies";
import { isLandUnit, landIn } from "@/lib/config/landArea";

const money = (n: number) => `UGX ${Math.round(n).toLocaleString()}`;
const NEW_DAYS = 14;

export function PropertyShopProductCard({
  product,
  className,
  productUrlPrefix = "",
}: {
  product: ShopProduct & Record<string, any>;
  className?: string;
  productUrlPrefix?: string;
}) {
  const d = product.property || {};
  const rent = isForRent(product);
  const isLand = d.propertyType === "land";

  const images: string[] = Array.isArray(product.images) && product.images.length
    ? product.images
    : product.imageUrl
      ? [product.imageUrl]
      : [PROPERTY_PLACEHOLDER_IMAGE];

  const listedAt = Number(d.listedAt || 0);
  const isNew = listedAt > 0 && Date.now() - listedAt < NEW_DAYS * 86400000;
  const gone = d.status === "sold" || d.status === "let";
  const underOffer = d.status === "under_offer";

  const ribbon = gone
    ? { text: d.status === "sold" ? "SOLD" : "LET", tone: "#64748b" }
    : underOffer
      ? { text: "UNDER OFFER", tone: "#f59e0b" }
      : isNew
        ? { text: "JUST LISTED", tone: "#dc2626" }
        : rent
          ? { text: "TO LET", tone: "#16a34a" }
          : null;

  const href = `${productUrlPrefix}/property/${product.slug || product._id}`;

  // The four a buyer or tenant reads without thinking — and they differ by
  // what the thing IS. Nobody asks a plot how many bathrooms it has, and a
  // shop with "— BEDS — BATHS" reads as a broken card, not an empty one.
  const isCommercial = ["shop", "office", "warehouse", "building"].includes(d.propertyType);
  // A measured plot: the unit it was given in, the other measure under it. A
  // plot listed before decimals kept its m² in sizeSqm.
  const plotUnit = isLandUnit(d.plotUnit) ? d.plotUnit : "m2";
  const plotM2 = Number(d.plotM2) > 0 ? Number(d.plotM2) : isLand && !d.plotSize && Number(d.sizeSqm) > 0 ? Number(d.sizeSqm) : 0;
  const foreign = cleanForeignPrice(d.foreignPrice);
  const specs: { icon: LucideIcon; value: string; key: string; plain?: boolean }[] = isLand
    ? [
        plotM2
          ? { icon: LandPlot, value: landIn(plotM2, plotUnit), key: landIn(plotM2, plotUnit === "m2" ? "decimal" : "m2"), plain: true }
          : { icon: LandPlot, value: d.plotSize || "—", key: "plot" },
        { icon: ScrollText, value: d.tenure ? tenureLabel(d.tenure) : "—", key: "tenure" },
        { icon: ScrollText, value: d.titleStatus === "ready" ? "Ready" : d.titleStatus === "processing" ? "In process" : "—", key: "title" },
        { icon: MapPin, value: d.location?.area || "—", key: "where" },
      ]
    : isCommercial
      ? [
          { icon: Maximize2, value: d.sizeSqm ? `${d.sizeSqm}` : "—", key: "m²" },
          { icon: Ruler, value: d.floors ? String(d.floors) : "1", key: d.floors && d.floors > 1 ? "floors" : "floor" },
          { icon: Car, value: d.parking ? String(d.parking) : "—", key: "parking" },
          { icon: MapPin, value: d.location?.area || "—", key: "where" },
        ]
      : [
          { icon: BedDouble, value: d.bedrooms ? String(d.bedrooms) : "—", key: "beds" },
          { icon: Bath, value: d.bathrooms ? String(d.bathrooms) : "—", key: "baths" },
          { icon: Maximize2, value: d.sizeSqm ? `${d.sizeSqm}` : "—", key: "m²" },
          { icon: Car, value: d.parking ? String(d.parking) : "—", key: "parking" },
        ];

  const chips = [
    d.furnished ? "Furnished" : null,
    !rent && d.tenure ? `${tenureLabel(d.tenure)} title` : null,
    d.negotiable !== false ? "Negotiable" : null,
    rent && d.depositMonths ? `${d.depositMonths} month${d.depositMonths === 1 ? "" : "s"} deposit` : null,
  ].filter(Boolean) as string[];
  const rules = rent ? houseRuleChips(d.rules).join(" · ") : "";

  return (
    <Link
      href={href}
      className={`group block overflow-hidden rounded-xl border bg-white transition-all hover:shadow-lg dark:bg-neutral-900 ${className || ""}`}
      style={{ borderColor: "rgba(0,0,0,0.08)", opacity: gone ? 0.72 : 1 }}
    >
      {/* Landscape — a house needs its compound in frame */}
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-neutral-100 dark:bg-neutral-800">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={images[0]}
          alt={product.name}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
        {ribbon && (
          <span
            className="absolute left-0 top-3 rounded-r px-2.5 py-1 text-[10px] font-bold tracking-widest text-white"
            style={{ backgroundColor: ribbon.tone }}
          >
            {ribbon.text}
          </span>
        )}
        {images.length > 1 && (
          <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-medium text-white">
            <Camera className="h-3 w-3" /> {images.length}
          </span>
        )}
        <span className="absolute bottom-2 left-2 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          {propertyTypeLabel(d.propertyType)}
        </span>
      </div>

      <div className="p-3.5">
        <p className="text-xl font-extrabold tabular-nums text-neutral-900 dark:text-neutral-50">
          {money(Number(product.price) || 0)}
          {rent && <span className="text-xs font-medium text-neutral-500"> / {intervalPer(d.rentInterval)}</span>}
        </p>
        {foreign && (
          <p className="text-[13px] font-semibold tabular-nums text-neutral-600 dark:text-neutral-300">
            {formatIn(foreign.amount, foreign.code)}
            {rent && <span className="text-[11px] font-medium text-neutral-500"> / {intervalPer(d.rentInterval)}</span>}
          </p>
        )}

        <h3 className="mt-1 line-clamp-1 text-[15px] font-bold leading-tight text-neutral-900 dark:text-neutral-50">
          {product.name}
        </h3>

        {(d.location?.area || d.location?.landmark) && (
          <p className="mt-0.5 line-clamp-1 flex items-center gap-1 text-[12px] text-neutral-500">
            <MapPin className="h-3 w-3 shrink-0" />
            {[d.location?.area, d.location?.landmark].filter(Boolean).join(" · ")}
          </p>
        )}

        {rules && (
          <p className="mt-0.5 flex min-w-0 items-center gap-1 text-[12px] text-neutral-600 dark:text-neutral-300" title={rules}>
            <Users className="h-3 w-3 shrink-0" />
            <span className="truncate">{rules}</span>
          </p>
        )}

        {/* The spec strip */}
        <div className="mt-3 flex border-y border-neutral-200 dark:border-neutral-800">
          {specs.map((s, i) => (
            <div
              key={s.key}
              className={`flex-1 py-2 text-center ${i < specs.length - 1 ? "border-r border-neutral-200 dark:border-neutral-800" : ""}`}
            >
              <p className="line-clamp-1 text-[12px] font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">{s.value}</p>
              <p className={s.plain ? "line-clamp-1 text-[10px] tabular-nums text-neutral-500" : "text-[9px] uppercase tracking-wide text-neutral-500"}>{s.key}</p>
            </div>
          ))}
        </div>

        <div className="mt-2.5 flex items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap gap-1">
            {chips.slice(0, 2).map((c) => (
              <span
                key={c}
                className="rounded-full border px-2 py-0.5 text-[11px] text-neutral-600 dark:border-neutral-700 dark:text-neutral-300"
                style={{ borderColor: "rgba(0,0,0,0.12)" }}
              >
                {c}
              </span>
            ))}
          </div>
          <span className="shrink-0 text-[12px] font-semibold text-violet-600 dark:text-violet-400">
            {rent ? "View & enquire" : "View details"}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default PropertyShopProductCard;
